/**
 * Gallery data.
 *
 * Two rules drive the shape of this hook:
 *
 *  1. Never request a URL for a photo that is not on screen or about to be.
 *  2. Never request the same URL twice — signed thumbnail URLs are stable for
 *     an hour, so React Query's cache and the browser's HTTP cache both hit.
 *
 * Thumbnail URLs are fetched one page at a time rather than per tile. A page of
 * 100 photos costs one request instead of a hundred, and each page's URLs are
 * cached under their own key so loading page 7 does not re-sign pages 1–6.
 */

import { useCallback, useMemo, useRef } from "react";
import {
  useInfiniteQuery,
  useQueries,
  useQueryClient,
} from "@tanstack/react-query";
import type { GallerySort } from "@fc/core";
import { trpc, trpcClient } from "../../lib/trpc.js";
import { processingPollDelay } from "../../lib/pageActivity.js";
import { imageAccept } from "../../lib/imageSupport.js";
import type { MediaRef } from "../../lib/useDecryptedUrl.js";

export interface GalleryItem {
  id: string;
  name: string;
  kind: "photo" | "video";
  /** The original's type — what a download should be named and saved as. */
  mime: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  takenAt: string;
  uploadedAt: string;
  uploaderId: string | null;
  folderId: string | null;
  status: "uploading" | "processing" | "ready" | "failed";
  /** Why it failed, in words meant for the person who uploaded it. */
  processingError: string | null;
  caption: string | null;
  favorited: boolean;
  /** Sealed with the group key; null for anything stored before encryption. */
  wrappedFileKey: string | null;
}

export interface GalleryFilters {
  folderId?: string;
  kind?: "photo" | "video";
  /**
   * Whose uploads to show.
   *
   * The screen has always passed this and the server has always understood
   * it — the field was simply missing here, so it was dropped on the way
   * past and "Added by" quietly did nothing. `filters` reaches this hook as
   * a variable rather than a literal, so no excess-property check ever
   * fired to say so.
   */
  uploaderId?: string;
  year?: number;
  favoritesOnly?: boolean;
}

/**
 * The filter as the server wants it: only the keys actually set.
 *
 * Pulled out of the hook because this is where “Added by” was lost. The
 * screen passed uploaderId, the API schema accepted it, and the SQL applied
 * it — this object was built without it, so it never left the browser.
 * Nothing failed anywhere; the dropdown simply did nothing.
 *
 * The reason it went unnoticed: `filters` reaches the hook as a variable
 * rather than an object literal, so no excess-property check ever fired to
 * say the field was unknown here.
 *
 * Testable on its own now, so the next filter added either travels or fails
 * a test.
 */
export function toServerFilter(filters: GalleryFilters) {
  return {
    ...(filters.folderId ? { folderId: filters.folderId } : {}),
    ...(filters.kind ? { kind: filters.kind } : {}),
    ...(filters.uploaderId ? { uploaderId: filters.uploaderId } : {}),
    ...(filters.year ? { year: filters.year } : {}),
    ...(filters.favoritesOnly ? { favoritesOnly: true } : {}),
  };
}

const PAGE_SIZE = 100;

/**
 * How many file ids may travel in one signing request.
 *
 * Bounded by URL length, not by anything server-side: the ids go in the query
 * string, and a hundred of them is a 3,800-character URL against a 2,048
 * limit. Twenty is about 860 characters, which leaves room for the batching
 * layer to combine a few and still fit.
 */
const THUMB_CHUNK = 20;

export function useGallery(
  groupId: string,
  filters: GalleryFilters,
  sort: GallerySort = "taken_desc",
) {
  const queryClient = useQueryClient();
  const pendingPoll = useRef({ key: "", since: 0 });

  const filter = useMemo(
    () => toServerFilter(filters),
    [
      filters.folderId,
      filters.kind,
      filters.uploaderId,
      filters.year,
      filters.favoritesOnly,
    ],
  );

  const list = useInfiniteQuery({
    queryKey: ["gallery", groupId, filter, sort],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      trpcClient.media.list.query({
        groupId,
        filter,
        sort,
        cursor: pageParam,
        limit: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30_000,
    // Poll only visible pending work, backing off for long-running jobs.
    refetchInterval: (query) => {
      const pending = query.state.data?.pages.flatMap((page) =>
        page.items.filter((item) => item.status === "processing" || item.status === "uploading"),
      ) ?? [];
      const key = pending.map((item) => item.id + item.status).join(",");
      if (key !== pendingPoll.current.key) pendingPoll.current = { key, since: Date.now() };
      return key ? processingPollDelay(Date.now() - pendingPoll.current.since) : false;
    },
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const pages = list.data?.pages ?? [];

  /**
   * Signing requests, in chunks of twenty.
   *
   * This was one request per page, and a page is a hundred photographs. The
   * file ids travel in the query string, so a hundred of them — quoted,
   * comma-separated and URL-encoded — is a 3,800-character URL, against the
   * 2,048 the client is configured to allow. A single call cannot be split
   * any further by the batching layer, so the whole request failed.
   *
   * The failure is total and silent: every tile on that page loses its
   * thumbnail URL at once, so a group looks entirely broken while its
   * thumbnails sit in storage, intact and unreferenced. It only happens past
   * about fifty files, which is why small groups were fine and large ones were
   * uniformly ruined — and why rebuilding a preview never helped: nothing was
   * wrong with the previews.
   *
   * Twenty ids is roughly 860 characters, which leaves room for the batching
   * layer to combine a few of these and still stay inside the limit.
   */
  const urlChunks = useMemo(() => {
    const chunks: { pageIndex: number; items: GalleryItem[] }[] = [];
    pages.forEach((page, pageIndex) => {
      for (let start = 0; start < page.items.length; start += THUMB_CHUNK) {
        chunks.push({
          pageIndex,
          items: page.items.slice(start, start + THUMB_CHUNK),
        });
      }
    });
    return chunks;
  }, [pages]);

  const urlQueries = useQueries({
    queries: urlChunks.map((chunk) => {
      const page = { items: chunk.items };
      const fileIds = page.items.map((item) => item.id);
      // A file that was still processing has no variants yet, so the server
      // returned no URL for it. Including the ready-count in the key means the
      // batch is re-signed once processing finishes, instead of serving the
      // cached "no thumbnail" answer for the next 50 minutes.
      const readyCount = page.items.filter(
        (item) => item.status === "ready",
      ).length;

      return {
        queryKey: ["thumbUrls", groupId, fileIds, readyCount],
        enabled: fileIds.length > 0,
        // `accept` is sent explicitly: a fetch for JSON advertises a wildcard Accept, so
        // the server cannot tell from the request header whether this browser
        // can render AVIF. Detection is cached, so this resolves immediately
        // after the first call.
        queryFn: async () =>
          trpcClient.media.urls.query({
            groupId,
            fileIds,
            variant: "thumb" as const,
            accept: await imageAccept(),
          }),
        // Signed URLs are quantised to a one-hour window server-side, so
        // holding them for 50 minutes never serves an expired link.
        staleTime: 50 * 60_000,
        gcTime: 60 * 60_000,
      };
    }),
  });

  /*
   * A URL and the key that opens it, together.
   *
   * The pair travels as one because separating them is how a tile ends up with
   * bytes it cannot read: the URL is useless alone, and the key is meaningless
   * without the object it belongs to.
   */
  const thumbRefs = useMemo(() => {
    const map = new Map<string, MediaRef>();
    for (const query of urlQueries) {
      for (const entry of query.data?.urls ?? []) {
        if (entry.url) {
          map.set(entry.fileId, {
            url: entry.url,
            wrappedFileKey: entry.wrappedFileKey ?? null,
            width: entry.width,
            height: entry.height,
          });
        }
      }
    }
    return map;
  }, [urlQueries]);

  /*
   * Files we now know have no thumbnail, as distinct from files we have not
   * asked about yet.
   *
   * `thumbRefs` drops anything without a URL, so those two cases arrived at the
   * grid identically — as an absent entry — and the tile drew a skeleton for
   * both. A skeleton means "loading", so a group whose signing request failed
   * showed 77 tiles quietly pretending to load, for ever, with nothing in the
   * console and nothing in the interface to say otherwise.
   *
   * An errored query marks its whole page, because in that case we learned
   * nothing about any of its files.
   */
  const thumbsUnavailable = useMemo(() => {
    const unavailable = new Map<string, string>();
    urlQueries.forEach((query, index) => {
      const chunk = urlChunks[index];
      if (!chunk) return;
      const page = { items: chunk.items };
      if (query.isError) {
        /*
         * The reason, carried through, because the tile has to be able to say
         * it. A request that was rate-limited, refused or never answered is a
         * completely different problem from a photograph with no thumbnail,
         * and both used to render as the same grey box — which is precisely
         * why neither could be diagnosed by anybody looking at the screen.
         */
        const status = (
          query.error as { data?: { httpStatus?: number } } | null
        )?.data?.httpStatus;
        const code = (query.error as { data?: { appCode?: string } } | null)
          ?.data?.appCode;
        const label = status
          ? `URL-${String(status)}${code ? `-${code}` : ""}`
          : "URL-FAILED";
        for (const item of page.items) unavailable.set(item.id, label);
        return;
      }
      for (const entry of query.data?.urls ?? []) {
        if (entry.url) continue;
        const reason = (entry as { reason?: string }).reason;
        unavailable.set(
          entry.fileId,
          reason === "sign-failed" ? "SIGN-FAILED" : "NO-VARIANT",
        );
      }
    });
    return unavailable;
  }, [urlQueries, urlChunks]);

  const items = useMemo(
    () => pages.flatMap((page) => page.items) as unknown as GalleryItem[],
    [pages],
  );

  /**
   * Full-size URL for the lightbox, fetched on demand and cached. Returning the
   * promise lets the viewer preload neighbours without rendering them.
   */
  const previewUrl = useCallback(
    (fileId: string) =>
      queryClient.fetchQuery({
        queryKey: ["previewUrl", groupId, fileId],
        queryFn: async () =>
          trpcClient.media.url.query({
            groupId,
            fileId,
            variant: "preview" as const,
            accept: await imageAccept(),
          }),
        staleTime: 50 * 60_000,
      }),
    [queryClient, groupId],
  );

  return {
    items,
    thumbRefs,
    thumbsUnavailable,
    previewUrl,
    isLoading: list.isLoading,
    isError: list.isError,
    error: list.error,
    hasNextPage: list.hasNextPage,
    isFetchingNextPage: list.isFetchingNextPage,
    fetchNextPage: list.fetchNextPage,
    refetch: list.refetch,
  };
}

/** Month buckets for the sticky date headers. */
export function useTimeline(groupId: string, filters: GalleryFilters) {
  return useQueries({
    queries: [
      {
        ...trpc.media.timeline.queryOptions({
          groupId,
          filter: {
            ...(filters.folderId ? { folderId: filters.folderId } : {}),
            ...(filters.kind ? { kind: filters.kind } : {}),
          },
        }),
        staleTime: 60_000,
      },
    ],
  })[0]!;
}

/**
 * Group items into month sections.
 *
 * Done client-side over already-loaded items rather than as a second query, so
 * headers appear the instant a page arrives instead of one round trip later.
 */
export interface GallerySection {
  key: string;
  label: string;
  items: GalleryItem[];
}

export function groupByMonth(items: GalleryItem[]): GallerySection[] {
  const sections: GallerySection[] = [];
  let current: GallerySection | null = null;

  for (const item of items) {
    const date = new Date(item.takenAt);
    const key = `${date.getFullYear()}-${date.getMonth()}`;

    if (!current || current.key !== key) {
      current = {
        key,
        label: date.toLocaleDateString(undefined, {
          month: "long",
          year: "numeric",
        }),
        items: [],
      };
      sections.push(current);
    }
    current.items.push(item);
  }

  return sections;
}

/**
 * Section by album instead of by month.
 *
 * Unlike months, albums are not contiguous in a date-sorted list, so this
 * buckets rather than walking runs. Photos in no album collect at the end
 * rather than being dropped — "where did the rest go?" is a worse outcome than
 * an untidy final section.
 */
export function groupByAlbum(
  items: GalleryItem[],
  albums: { id: string; name: string; emoji: string | null }[],
): GallerySection[] {
  const byId = new Map(albums.map((album) => [album.id, album]));
  const buckets = new Map<string, GalleryItem[]>();

  for (const item of items) {
    const key = item.folderId ?? "__none__";
    const bucket = buckets.get(key);
    if (bucket) bucket.push(item);
    else buckets.set(key, [item]);
  }

  const sections: GallerySection[] = [];

  // Album order follows the album list, so the gallery and the Albums screen
  // agree on ordering.
  for (const album of albums) {
    const bucketItems = buckets.get(album.id);
    if (!bucketItems?.length) continue;
    sections.push({
      key: album.id,
      label: album.emoji ? `${album.emoji} ${album.name}` : album.name,
      items: bucketItems,
    });
  }

  // Anything whose album was deleted while the page was open.
  for (const [key, bucketItems] of buckets) {
    if (key === "__none__" || byId.has(key)) continue;
    sections.push({ key, label: "Album removed", items: bucketItems });
  }

  const loose = buckets.get("__none__");
  if (loose?.length) {
    sections.push({ key: "__none__", label: "Not in an album", items: loose });
  }

  return sections;
}
